import type { AiCapabilityPage, AiSlot } from "../registry/schema.js";
import { hasViDiacritics } from "../vi-normalizer.js";
import { retrievalKey } from "./page-retrieval.js";
import { renderRecentTurns, type InterpreterRecentTurn } from "./recent-turns.js";

/**
 * 🔴 US-AIV2-0051 S01 (@UC0170 @UC0151 @UC0173 · ADR-0009) — PROMPT của Người phiên dịch, SINH TỪ SỔ.
 *
 * Người phiên dịch là vai DUY NHẤT dùng LLM (@UC0170: LLM là bộ hiểu CHÍNH — không regex phân loại ý định).
 * Nó chỉ được làm bốn việc: chọn MỘT trang trong danh sách ĐƯỢC PHÉP · gạch chân chữ NGUYÊN VĂN cho từng ô ·
 * nói độ chắc · nếu kẹt giữa ĐÚNG HAI trang thì đưa hai ứng viên. Prompt này là PHÉP CHIẾU của Sổ: mọi chữ
 * về "việc trợ lý làm được" đến từ `AiCapabilityPage` (id · description · slots · examples) — không có câu
 * chữ nào về trang nằm ngoài danh sách truyền vào (tool-set đã lọc theo vai/cờ ở tầng trên, `@UC0152` — 0052).
 *
 * Vì sao ở `packages/domain` (thuần): ba nơi đọc cùng một prompt — cổng api (0051) · eval (0053) · bộ sinh
 * tài liệu; đặt ở api thì hai nơi kia phải import Nest. Không I/O, không thời gian ⇒ tất định ⇒ eval tái lập được.
 *
 * Vì sao có CACHE phần tĩnh: danh sách trang đổi rất hiếm (Sổ là dữ liệu commit), còn mỗi lượt chat gọi một
 * lần. Cache theo IDENTITY của mảng trang (WeakMap) — caller giữ mảng ổn định (Sổ đã lọc theo vai) thì render
 * đúng một lần; mảng mới (vai khác, cờ khác) thì render mới. Phần ĐỘNG (đang giữa form) nối vào sau, không
 * làm bẩn cache.
 *
 * 🔴 Khuôn JSON dưới đây phải KHỚP `aiEnvelopeSchema` (contracts, 0012). Domain không import contracts (chiều
 * phụ thuộc là contracts → domain), nên hai hằng `INTERPRETER_ENVELOPE_KEYS` / `INTERPRETER_SPAN_KEYS` được khoá
 * bằng test parity ở phía api (`ai-interpreter.service.spec.ts`: khoá của schema zod ≡ khoá ở đây). Thêm khoá
 * một bên mà quên bên kia ⇒ đỏ.
 */

/** Khoá cấp gốc của Phiếu — thứ tự cố định để prompt tất định. */
export const INTERPRETER_ENVELOPE_KEYS = ["kind", "page", "spans", "confidence", "ambiguities", "multi"] as const;
/** Khoá của một span — thứ LLM được phép trả. */
export const INTERPRETER_SPAN_KEYS = ["slot", "text", "start", "end"] as const;

/**
 * 🔴 US-AIV2-0054b (@UC0176 · review #881 M-5) — khoá của Phiếu do **ĐƯỜNG ỐNG DẪN XUẤT**, KHÔNG phải do LLM khai.
 *
 * `mapped` chỉ được `judgeSpan` gắn SAU khi đã quyết định span thuộc ô PHÂN LOẠI; nó **cố ý KHÔNG có trong prompt**
 * — nói cho model biết về một cờ mà nó không được đặt là mời nó đặt. Test parity ở api vì thế so
 * `INTERPRETER_SPAN_KEYS ∪ AI_INTERPRETER_DERIVED_SPAN_KEYS ≡ khoá aiSpanSchema`, **hai chiều**: thêm khoá vào
 * contract mà quên bên này ⇒ đỏ; và khai một khoá dẫn xuất mà nó LẠI xuất hiện trong prompt ⇒ cũng đỏ.
 *
 * ⚠️ Vì `mapped` KHÔNG vào chuỗi prompt nên `promptHash` **không đổi** ⇒ bộ bản ghi eval đông lạnh còn hiệu lực.
 * Đó là một ràng buộc, không phải may mắn: đừng đưa khoá dẫn xuất nào vào khuôn JSON của prompt.
 */
export const AI_INTERPRETER_DERIVED_SPAN_KEYS = ["mapped"] as const;

/**
 * Số câu ví dụ mỗi trang đưa vào prompt.
 *
 * 🔴 US-AIV2-0093 — từng là **3**, và đó là nguyên nhân đo được của `intent 25,7%` (23/09, 268 bản ghi thật: ví dụ #1-3 CÓ trong
 * prompt ⇒ model bỏ cuộc 2% · ví dụ #4+ BỊ CẮT ⇒ 33%, gấp 16 lần). ⇒ Trần không được về ≤3 (`prompt.test.ts` khoá).
 *
 * 🔴 AI-SMOOTH-4 (QĐ người dùng 03/11/2026 §3.1 — prompt ăn kiêng) — **8** câu/trang (`promptExamples`), sau khi bỏ bản không dấu sinh đôi.
 * Đo 04/11 (Sổ ~40 câu/trang): trước khi cắt, prompt 8 trang dài nhất **21,5K** ký tự, ca fail-open (không trang nào đạt ngưỡng tìm ⇒ cả
 * Sổ) **93,6K**; sau khi cắt **11,6K** / **37,4K** — trần kích thước khoá ở `prompt.test.ts`. Cái GIỮ: mọi câu vẫn ở chỉ mục tìm trang
 * (`page-retrieval.ts` đọc `llm.examples` ĐỦ) và ở bộ câu chuẩn eval. Cái MẤT, nói thẳng: bảng 2%/33% đo trên chính các câu ví dụ (bộ eval =
 * câu ví dụ), nên ca eval ở câu #9+ sẽ chấm kém đi — QĐ 03/11 đổi thước (`live` = binding test + người nghiệm thu, eval chỉ cảnh báo), và
 * hiểu hụt ngoài form có leo thang (AI-SMOOTH-5) đỡ. CHƯA đo bằng LLM thật (QĐ 03/11 tối: người dùng test tay). Thứ tự trong Sổ là thứ tự
 * ưu tiên — trừ câu phản ví dụ của trang ghi (`pinnedQuestions`), luôn đứng đầu.
 */
export const INTERPRETER_EXAMPLES_PER_PAGE = 8;

interface PromptExample {
  readonly text: string;
  /** Khoá so «cùng một câu» của chỉ mục tìm trang (bỏ dấu · bỏ dấu câu · hạ hoa). */
  readonly key: string;
}

/**
 * Câu ví dụ của trang SAU KHI bỏ bản sinh đôi, đủ thứ tự Sổ — cache theo IDENTITY mảng `llm.examples` (Sổ là dữ liệu commit, mảng là
 * singleton) vì phần tĩnh render lại MỖI lượt ở runtime (mảng trang tìm được là mảng mới — security review AI-SMOOTH-4 L1).
 */
const dedupedExamplesCache = new WeakMap<readonly string[], readonly PromptExample[]>();

/**
 * 🔴 AI-SMOOTH-4 — bỏ bản KHÔNG DẤU của một câu CÓ DẤU cùng trang (sinh đôi: `EXAMPLES_NEED_BOTH_DIACRITIC_VARIANTS` bắt Sổ khai cả hai cho chỉ
 * mục tìm trang và bộ eval; với model nó là cùng một câu in hai lần). «Cùng câu» = cùng `retrievalKey` — dấu câu không tính («ai chua dong
 * thang nay» là sinh đôi của «ai chưa đóng tháng này?», review goppy AI-SMOOTH-4 M2). Câu không dấu KHÔNG có bản có dấu là cách nói riêng
 * (`"add member"`, `"thu 200k"`) ⇒ GIỮ. «Không dấu» = `!hasViDiacritics` — CÙNG vị-từ với validator.
 */
function dedupedExamples(examples: readonly string[]): readonly PromptExample[] {
  const hit = dedupedExamplesCache.get(examples);
  if (hit !== undefined) return hit;
  const all = examples.map((text) => ({ text, key: retrievalKey(text), accented: hasViDiacritics(text) }));
  const accentedKeys = new Set(all.filter((e) => e.accented).map((e) => e.key));
  const kept = all.filter((e) => e.accented || !accentedKeys.has(e.key)).map(({ text, key }) => ({ text, key }));
  dedupedExamplesCache.set(examples, kept);
  return kept;
}

/**
 * 🔴 AI-SMOOTH-4 (review goppy HIGH-1 · giữ bản vá US-AIV2-0111 C06 @UC0320) — câu HỎI mà một trang GHI khai «không thuộc tôi»
 * (`llm.questionsNotHere`). C06 chặn câu hỏi mở nhầm trang ghi bằng cách để câu đó là ví dụ DƯƠNG của trang ĐỌC TRONG prompt (đo 28/09: có
 * câu đó trong prompt ⇒ READ-COLLECTIONS 3/3). Các câu ấy nằm cuối danh sách ví dụ nên trần 8 cắt mất ⇒ khi trang ghi có mặt trong bộ trang
 * của lượt, câu phản ví dụ của nó được ĐẨY LÊN ĐẦU danh sách của trang đọc sở hữu câu đó. Chỉ đẩy khi trang ghi có mặt: không có nó thì câu
 * hỏi không thể bị kéo về nó. Khoá theo `retrievalKey` ⇒ bản có dấu và không dấu cùng khoá, model thấy bản có dấu. Chỉ đẩy ở trang ĐỌC
 * (`pageLines`): C06 đúng khi câu là ví dụ dương của trang đọc — đẩy nó lên đầu một trang GHI là kéo câu hỏi về phía mở phiếu (review security
 * AI-SMOOTH-4 L3; validator so câu `questionsNotHere` bằng khoá khác, nên chốt ở đây không dựa vào nó). ⚠️ Phần hở ĐÃ CHẤP NHẬN: chỉ đẩy khi
 * trang ĐÃ KHAI có mặt — Sổ đổi làm một trang ghi CHƯA khai lọt vào top-8 của câu thì câu lại vắng mà không test nào đỏ (cố ý không có ca dò
 * BM25: nó đỏ khi Sổ đổi ở chỗ khác). Dò tay mỗi lần đổi Sổ — backlog `QNH-RETRIEVAL-PROBE`.
 */
function pinnedQuestions(pages: readonly AiCapabilityPage[]): ReadonlySet<string> {
  return new Set(pages.flatMap((p) => p.llm.questionsNotHere ?? []).map(retrievalKey));
}

const NO_PINS: ReadonlySet<string> = new Set();

/** Câu ví dụ IN VÀO PROMPT: câu phản ví dụ của trang ghi có mặt đứng đầu, rồi thứ tự Sổ; tối đa `INTERPRETER_EXAMPLES_PER_PAGE`. */
function promptExamples(examples: readonly string[], pinned: ReadonlySet<string>): readonly string[] {
  const deduped = dedupedExamples(examples);
  const first = deduped.filter((e) => pinned.has(e.key));
  const rest = deduped.filter((e) => !pinned.has(e.key));
  return [...first, ...rest].slice(0, INTERPRETER_EXAMPLES_PER_PAGE).map((e) => e.text);
}

/** Ngữ cảnh ĐỘNG: người dùng đang giữa form của một trang (@UC0173 — câu được phân bốn loại). */
export interface InterpreterFormContext {
  /** Trang đang mở form. */
  readonly page: string;
  /** Ô đang được hỏi (vắng khi form đang ở phiếu xác nhận). */
  readonly askedSlot?: string;
  /** Các ô đã có chữ — "sửa lại" là span cho một ô trong danh sách này. */
  readonly filledSlots: readonly string[];
}

export interface InterpreterPromptContext {
  readonly inForm?: InterpreterFormContext;
  /**
   * 🔴 US-AIV2-0024f (@UC0236) — vài lượt TRƯỚC của cùng hội thoại (cũ→mới), đã cắt cửa sổ.
   *
   * Caller chịu trách nhiệm: (a) cắt bằng `takeRecentTurns`, (b) cho đoạn này đi qua ĐÚNG scrubber
   * như câu mới — PII của lượt cũ cũng rời hệ thống. Vắng/rỗng ⇒ prompt Y HỆT trước task này.
   */
  readonly recentTurns?: readonly InterpreterRecentTurn[];
}

export interface InterpreterPromptBuilder {
  /** Prompt hệ thống đầy đủ = phần tĩnh (cache theo `pages`) + phần động (ctx). */
  system(pages: readonly AiCapabilityPage[], ctx?: InterpreterPromptContext): string;
  /** Số lần phần TĨNH được render — để test đo cache, không phải để logic đọc. */
  readonly staticRenders: number;
}

/**
 * 🔴 US-AIV2-0093 — phần CHÚ THÍCH KIỂU của một ô, **tách khỏi id**.
 *
 * `slotLabel` gộp `id (kiểu)` thành một chuỗi; nếu bọc cả chuỗi ấy trong nháy thì model chép nguyên
 * `nhom (group)` vào `"slot"` — vẫn sai, chỉ sai kiểu khác. Id đứng TRONG nháy (thứ phải chép),
 * chú thích đứng NGOÀI (thứ để hiểu). Xem lý lẽ ở `pageLines`.
 */
/**
 * 🔴 AI-SMOOTH-3 (QĐ người dùng 03/11/2026 §3.1) — NHÃN KHUÔN của prompt, export để test khuôn (`prompt.test.ts`) bám vào CHÍNH hằng của bên
 * sinh thay vì chép chữ: đổi lời một nhãn không làm đỏ test; phá HÌNH DẠNG (nhãn dính id · hai nhãn chung dòng · tên ô ra ngoài nháy) thì đỏ.
 */
/** Tên khối danh sách trang — câu luật «chép id từ …» gọi đúng tên này, đổi nhãn thì cả hai đổi theo (review goppy AI-SMOOTH-4 L4). */
const CATALOG_NAME = "DANH SÁCH TRANG ĐƯỢC PHÉP";
export const INTERPRETER_PROMPT_LABELS = Object.freeze({
  catalogHeading: `${CATALOG_NAME} (chỉ được chọn trong đây):`,
  slots: "ô của trang:",
  tier: "bậc việc:",
  examples: "người dùng hay nói:",
  /** US-AIV2-0110 S20 (@UC0268) — nhãn đứng trước câu hỏi của ô trong chú thích kiểu (lý lẽ ở `slotTypeHint`). */
  slotAsk: "ô trả lời câu hỏi: ",
  /** Khối giữa form (@UC0173): ô đang hỏi / ô đã điền là DỮ LIỆU của lượt — tráo hai ô là hỏng khuôn. */
  formAsked: "Ô đang hỏi:",
  formFilled: "Ô đã điền:",
});
const LABEL = INTERPRETER_PROMPT_LABELS;
const SLOT_ASK_LABEL = LABEL.slotAsk;

function slotTypeHint(s: AiSlot): string {
  const kieu = s.type === "enum" && s.choices ? `chọn: ${s.choices.join(" | ")}` : s.type;
  /*
   * 🔴 US-AIV2-0093 — NÓI RA Ô NÀO ĐƯỢC PHÉP BỎ TRỐNG.
   *
   * Đo được trên 12 ca thu lại: model nhét **cả câu người dùng** vào ô `nhom` (`"chuyen tien vao dau"`,
   * `"số tài khoản nhận tiền quỹ"`) ⇒ 4 `value_not_from_sentence`. Prompt có luật *"không thấy chữ thì
   * KHÔNG bịa span"*, nhưng nó **chưa bao giờ nói ô nào được phép trống** — model thấy một ô và cố điền,
   * đó là hành vi hợp lý của một người làm việc chăm chỉ.
   *
   * `autofill` nghĩa là **hệ thống tự suy** (vd `sole-active`: người chỉ ở một nhóm ⇒ lấy nhóm đó).
   * Nói thẳng "hệ thống tự điền" rẻ hơn hẳn việc thêm một câu luật thứ N mà model phải nhớ áp cho
   * đúng ô — luật ở xa, nhãn thì ngay cạnh tên ô.
   */
  /*
   * 🔴 US-AIV2-0110 S20 (@UC0268) — Ô có `ask` thì IN CÂU HỎI ĐÓ (trừ ô tự điền và ô enum, xem dưới).
   *
   * ⚠️ ĐÍNH CHÍNH: từ 0093 tới S20, chú thích ở đây đã viết câu trên như thể mã làm vậy — mã **chưa bao
   * giờ** in `ask` (`git log -S` xác nhận). Chú thích nói dối về mã; ô chỉ in MÃ + kiểu: `"nguon" (text)`.
   *
   * Đo được (S03, hai lượt thu): model không biết `nguon` là TÊN nhà tài trợ, `noiDung` là chi cho VIỆC GÌ
   * ⇒ gạch CẢ CÂU (kể cả số tiền) vào ô chữ, bỏ trống `soTien` — 9 ca trượt ở INC-RECORD · EXP-F-NOALLOC ·
   * EXP-F-EQUAL. Thí nghiệm S03b (thêm một câu giải nghĩa ô vào mô tả 3 trang) sửa 8/9 ⇒ nguyên nhân là
   * "ô thiếu nghĩa". `ask` ("Tiền từ đâu? (tên nhà tài trợ / ngân hàng / khoản hoàn)") là câu Sổ vốn dùng để
   * hỏi NGƯỜI; nó mô tả đúng thứ cần gạch chân, và dùng lại nó rẻ hơn viết một mô tả thứ hai (sẽ trôi khỏi nhau).
   *
   * HÌNH DẠNG (bài học 0093: model chép nhãn/ví dụ làm tên ô khi ranh giới mờ):
   *  · câu hỏi đứng TRONG ngoặc của chú thích, SAU kiểu, KHÔNG trong nháy — nháy là của tên ô, thứ duy nhất
   *    được chép vào `"slot"`; câu hỏi trong nháy sẽ trông như một tên ô nữa;
   *  · có nhãn `ô trả lời câu hỏi:` để câu hỏi đọc ra là NGHĨA của ô, không phải chữ để chép vào `"text"`;
   *  · ngăn bằng ` — ` như nhãn "không bắt buộc" sẵn có (câu hỏi trước, nhãn sau). Ca `@UC0268` khoá: câu
   *    hỏi không chứa `"` · `|` · `{}` · ` — `, ngoặc cân, và tập token trong nháy của dòng ô ĐÚNG bằng tập id ô.
   *  · chỉ in `ask` GỐC — `clarify`/`emptyAsk`/`duplicateAsk`/`partialAsk` là câu cho TÌNH HUỐNG hội thoại,
   *    không phải nghĩa của ô.
   *
   * Ô enum KHÔNG in `ask`: câu hỏi của ô phân loại diễn đạt lựa chọn bằng chữ KHÁC `choices` (READ-PEOPLE-MINE
   * hỏi "nhóm của bạn" mà lựa chọn là "nhóm của tôi"; READ-FUND-MONEY hỏi "các khoản chi" mà lựa chọn là
   * "danh sách chi") ⇒ in ra là mời model chép cách nói ngoài danh sách ⇒ `value_not_in_choices`, đúng bệnh
   * 0053c đã vá. `chọn: a | b` đã là nghĩa đầy đủ của ô enum.
   */
  /*
   * 🔴 US-AIV2-0095 — Ô TỰ ĐIỀN: nói "ĐỪNG gạch chân", KHÔNG nói "chỉ gạch chân khi câu nói rõ".
   *
   * Nhãn cũ (#926) đặt model vào thế PHẢI PHÁN ĐOÁN *"câu này có nói rõ chưa?"*, và khi không chắc
   * nó điền một thứ cho an toàn — cụ thể là **chép chính tên ô vào ô giá trị** (`text: "nhom"`).
   * Đo trên 474 bản ghi: **68/80 ca chép-tên-ô rơi vào ô có `autofill`** (`nhom` 51 · `dot` 9 ·
   * `quy` 5 · `khoan` 3).
   *
   * Vì sao BỎ HẲN là đúng chứ không phải mất mát: docstring của `aiSlotAutoResolves` nói rõ *"Người
   * làm hỏi resolver cho ô này KỂ CẢ khi người dùng chưa nói gì"* ⇒ ô tự-điền **không cần span** để
   * chạy. Và chỉ **5/474 câu** của bộ chuẩn nêu tên nhóm cụ thể ⇒ cái mất rất nhỏ, cái được là 68 ca.
   *
   * Ba biến thể đã đo trên 8 trang có ô (không phải 3 — mẫu 3 trang từng cho span=0 gây hiểu nhầm):
   *   nền "chỉ gạch chân khi NÓI RÕ"       : intent 86,91% · span 7,22% · safety 44
   *   A   "ĐỪNG gạch chân ô này"           : intent 87,95% · span 8,03% · safety 32  ← chọn
   *   C   "chỉ nếu nêu TÊN RIÊNG (vd lớp 3A)": intent 84,52% · span 8,15% · safety 12 (trên 3 trang)
   */
  if (s.autofill !== undefined) return `(${kieu} — hệ thống tự biết, ĐỪNG gạch chân ô này)`;
  const nghia = s.type === "enum" ? "" : ` — ${SLOT_ASK_LABEL}${s.ask}`;
  if (s.required === false) return `(${kieu}${nghia} — không bắt buộc)`;
  return `(${kieu}${nghia})`;
}

/**
 * 🔴🔴 US-AIV2-0053c (19/09) — **ID PHẢI ĐỨNG TRẦN**, nhãn bậc KHÔNG được dính vào nó.
 *
 * Bản trước in `- ONB-CREATE-GROUP [T1]: …` trong khi khối hình dạng phong bì dặn
 * `"page": "<id trang trong DANH SÁCH>"`. Hai câu đó **mâu thuẫn**: "id trong danh sách" nhìn ra đúng là
 * `ONB-CREATE-GROUP [T1]`. Đo được, lượt eval thật ĐẦU TIÊN của repo (12/12 ca, `gpt-5-nano`):
 * model trả `"page": "ONB-CREATE-GROUP [T1]"` — **nó tuân thủ chính xác thứ ta dặn** — rồi
 * `AI_PAGE_ID_RE = /^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)*$/` từ chối ⇒ `envelope_invalid` ⇒ vi phạm safety.
 * Kết quả: **intent 0 bps / 12 vi phạm safety**, tức trang KHÔNG THỂ đạt ngưỡng vì một dấu ngoặc vuông.
 *
 * ⚠️ Đây KHÔNG phải chuyện riêng của eval: runtime dùng **cùng bộ dựng prompt** này. Không ai thấy suốt
 * 50 trang / 442 ca vì **mọi int-spec đều stub người phiên dịch** — chưa phép đo nào cho đầu ra LLM THẬT
 * đi qua schema THẬT. Lưới dày, nhưng đều nằm sau cái stub.
 *
 * Nhãn bậc vẫn cần cho model (T1/T2 khác nhau về hệ quả), nên nó **chuyển xuống dòng thuộc tính**, chỗ
 * không ai nhầm là một phần của id. Sửa schema cho nhận `[T1]` là **đổi bên bị hỏng**: schema đang đúng.
 */
/**
 * 🔴🔴 US-AIV2-0084 (21/09) — **NHÃN BẬC KHÔNG ĐƯỢC DÙNG CHUNG TỪ VỰNG VỚI `kind`.**
 *
 * Cùng MỘT hình dạng lỗi với ca `[T1]` ghi ở docblock trên, chỉ đổi chỗ đứng: ở đó một nhãn bậc dính vào
 * **id**; ở đây một nhãn bậc trùng chữ với một **giá trị hợp lệ của khoá khác**.
 *
 * Đo được trên 40 bản ghi LLM thật (`gpt-5-nano`, 7 trang, 21/09): chữ `redirect` xuất hiện **30 lần** trong
 * prompt dưới dạng `bậc: redirect`, còn `kind` là tập ĐÓNG `page | inFormAnswer | cancel | unknown` —
 * **không có `redirect`**. Model trả `"kind": "redirect"` ở **5/40** lượt. Hậu quả nhân đôi, và cả hai đều
 * đánh vào đúng hai chiều mà cổng eval gác:
 *   · `aiEnvelopeSchema` `.strict()` từ chối ⇒ `envelope_invalid` ⇒ **vi phạm safety** (zero-tolerance);
 *   · `replay()` đọc `env.kind === "page" ? env.page : null` ⇒ trang **coi như chọn sai** dù model đã gọi
 *     ĐÚNG TÊN trang. Ca đo rõ nhất: `GRP-OWNERSHIP` chọn đúng trang **5/5** mà chấm ra **intent 0 bps**.
 *
 * ⇒ Bậc vẫn cần cho model (T1/T2 khác nhau về hệ quả), nhưng **in bằng từ vựng của chính nó** (`bậc việc`)
 * và với `redirect` thì nói ra ĐIỀU NÓ NGHĨA LÀ — "chuyển sang màn" — thay vì ném lại một từ khoá mà model
 * đang phải điền ở một khoá khác. Sửa schema cho nhận `"redirect"` là **đổi bên bị hỏng**: schema đang đúng
 * (`kind` mô tả LOẠI CÂU TRẢ LỜI, không mô tả bậc của trang).
 */
const TIER_LABEL: Readonly<Record<AiCapabilityPage["tier"], string>> = Object.freeze({
  read: "đọc",
  T1: "T1",
  T2: "T2",
  redirect: "chuyển màn",
});

/**
 * 🔴🔴 US-AIV2-0110 S23 (@UC0271 · QĐ PO 26/09 câu H(2)) — **ID ĐỨNG MỘT MÌNH TRÊN DÒNG CỦA NÓ**, mô tả xuống dòng dưới.
 *
 * Bản trước in `- READ-PEOPLE-MINE: Xem NGƯỜI trong nhóm…` và khuôn JSON dặn lấy *"phần NGAY SAU '- ' và TRƯỚC ':'"*.
 * Đo được ở CẢ HAI lượt thu S20 (26/09, `gpt-5.4-nano`, cùng `promptHash`): câu `"mình phải đóng gì tháng này"` ⇒
 * `"page": "READ-PEOPLE-MINE: Đọc NGƯỜI trong nhóm và việc của chính mình: nhóm có…"` — **cả dòng mục**, 900+ ký tự,
 * và còn đổi `Xem` → `Đọc` (model VIẾT LẠI dòng, không chép id) ⇒ `too_big`/`invalid_string @ page` ⇒ `envelope_invalid`
 * ⇒ vi phạm RÒ RỈ ⇒ cổng đỏ. Trước S20 cùng câu ra đúng `READ-PEOPLE-MINE` (conf 0,62 — một ca vốn đã chông chênh).
 *
 * Vì sao S20 lật nó dù dòng READ-PEOPLE-MINE KHÔNG đổi một ký tự: S20 chỉ đổi dòng ô của 3 trang khác trong top-8 của câu
 * (READ-FUND-MONEY · COL-DEADLINE · PAY-REPORT), mỗi ô thêm `ô trả lời câu hỏi: …` — **thêm dấu `:` vào prompt**. Mà `:`
 * là cái neo DUY NHẤT nói "id kết thúc ở đâu", trong khi chính mô tả READ-PEOPLE-MINE đã có `:` thứ hai (`…chính mình: nhóm
 * có…`) và nhắc tên 3 trang khác. Neo bằng một ký tự xuất hiện khắp nơi là neo yếu; đây là GIẢ THUYẾT (chưa gọi LLM để tách
 * nó khỏi nhiễu) — nhưng cách sửa dưới không phụ thuộc giả thuyết đúng hay sai.
 *
 * Sửa bằng HÌNH DẠNG (bài học 0093 — không thêm câu luật thứ N): dòng mục CHỈ còn id. Khi đó hành vi hỏng đo được —
 * *"chép nguyên dòng mục"* — cho ra ĐÚNG id; không còn ranh giới nào phải đoán. Mô tả xuống dòng thụt lề, **không nhãn**
 * (mỗi nhãn tiếng Việt là một ứng viên "tên ô" nữa: model đã lấy `bậc việc`, `người dùng hay nói` làm `slot` — 0097).
 * Khuôn JSON thôi nhắc `':'`.
 *
 * ⚠️ Vế CŨ của 0053c GIỮ NGUYÊN: không nhãn bậc nào dính vào id — nay còn chặt hơn, vì dòng id không có gì ngoài id.
 */
function pageLines(p: AiCapabilityPage, pinned: ReadonlySet<string>): string[] {
  const examples = promptExamples(p.llm.examples, p.tier === "read" ? pinned : NO_PINS).map((e) => `"${e}"`);
  const lines = [`- ${p.id}`, `    ${p.llm.description}`];
  /**
   * Ô `(không có)` nói trang KHÔNG khai ô nào, nhưng đo được nó chưa đủ ngăn model bịa: trên 40 bản ghi,
   * các trang 0 ô vẫn nhận span với `slot` tự chế (`text`, `ô`, `tk`, thậm chí chính id trang) ⇒ mỗi cái là
   * một `slot_not_in_page`. Nên ở trang 0 ô, nói thẳng HÀNH ĐỘNG phải làm (`"spans": []`) thay vì để model
   * tự suy ra từ một danh sách rỗng.
   */
  /*
   * 🔴 US-AIV2-0093 — TÁCH DÒNG, và tên ô in trong DẤU NHÁY.
   *
   * Hồi quy ĐO ĐƯỢC khi bắt đầu in ĐỦ ví dụ: model lấy **`"bậc việc"`**, **`"ô"`**, và cả câu ví dụ
   * (`"stk nhóm"`) làm tên ô ⇒ `invalid_string @ spans.[].slot` **8/12 ca** của trang đầu thu lại.
   * Nguyên nhân: cả ba thứ — nhãn, tên ô, câu ví dụ — trước đây dùng CÙNG ký tự ngăn `·` trên những
   * dòng trông giống nhau, nên ranh giới "đâu là nhãn, đâu là giá trị" biến mất khi khối ví dụ dài ra.
   *
   * Sửa bằng HÌNH DẠNG, không bằng một câu luật thứ N: mỗi thứ một dòng có tiền tố riêng, tên ô đặt
   * trong nháy để nó trông giống hệt thứ model phải chép vào `"slot"`, và ví dụ ngăn bằng `|` — ký tự
   * KHÔNG xuất hiện ở dòng ô. Câu luật *"slot CHỈ được là tên in ở dòng ô"* nay có một cái neo nhìn thấy được.
   */
  lines.push(
    p.slots.length > 0
      ? `    ${LABEL.slots} ${p.slots.map((sl) => `"${sl.id}" ${slotTypeHint(sl)}`).join(", ")}`
      : `    ${LABEL.slots} (KHÔNG có ô nào ⇒ "spans": [])`,
  );
  lines.push(`    ${LABEL.tier} ${TIER_LABEL[p.tier]}`);
  if (examples.length > 0) lines.push(`    ${LABEL.examples} ${examples.join(" | ")}`);
  /*
   * 🔴 US-AIV2-0111 C06 (@UC0320) — `llm.questionsNotHere` CỐ Ý KHÔNG vào prompt (cùng lý như `clarifyExamples`). ĐO 28/09 trên bộ
   * thu eval (gpt-5.4-nano, prompt runtime), câu *"có ai báo đã chuyển tiền chưa"* (có dấu / không dấu), 3 lượt mỗi biến thể:
   *   · KHÔNG in dòng âm (+ câu là ví dụ READ-COLLECTIONS): có dấu ⇒ READ-COLLECTIONS 3/3 · không dấu ⇒ PAY-CONFIRM-DECIDE 3/3 (hỏi "lời báo của ai")
   *   · in MỘT câu âm ở PAY-REPORT: có dấu ⇒ unknown · không dấu ⇒ PAY-REPORT (conf 0,2) — trang tự báo, đúng thứ phải tránh
   *   · in HAI câu âm (có + không dấu): cả hai ⇒ PAY-REPORT (conf tới 0,86) — model đọc dòng âm như một dòng VÍ DỤ
   *   · đổi chữ "chọn trang ĐỌC khác, KHÔNG chọn trang này": ⇒ PAY-REPORT 2/2
   * ⇒ với model này, "ví dụ âm" trong prompt KÉO câu về phía trang ghi. Trường vẫn BẮT BUỘC ở mọi trang T2 và phải là ví dụ của một
   * trang ĐỌC — nó là LƯỚI ĐO (eval chấm câu hỏi đó ở trang đọc), không phải lời dặn cho model. Ai muốn in lại vào prompt: đo lại
   * bảng trên trước (`scripts/collect-ai-eval-recordings.mjs`), đừng suy từ trực giác.
   */
  return lines;
}

/*
 * 🔴 US-AIV2-0089 (@UC0151) — khuôn mẫu này TỪNG TỰ MÂU THUẪN với luật viết bên dưới nó, và model
 * nghe theo MẪU. Ba tật đo được trên 268 bản ghi thật (97 ca trượt khuôn sau khi #918 gỡ 52):
 *
 *  · `"multi": []` in ở **dòng cuối, không dấu phẩy** ⇒ trông như tuỳ chọn ⇒ **35 ca** trả về thiếu
 *    ĐÚNG khoá đó (và 20 ca nữa thiếu cả cụm). Nay có một dòng nói thẳng "ĐỦ SÁU khoá".
 *  · `"spans": [ {…} ]` in **luôn có một phần tử** trong khi luật dưới viết "không thấy chữ thì
 *    KHÔNG bịa span" ⇒ **25 ca** điền đủ ô bằng `text: ""`, `end: 0` cho khỏi thiếu. Nay mẫu in
 *    `[]` kèm chú "mảng RỖNG là hợp lệ".
 *  · `"page"` viết chú "bỏ khoá khi cancel/unknown" **mà vẫn in ra một dòng `"page"`** ⇒ model hiểu
 *    "bỏ" thành LÀM RỖNG GIÁ TRỊ: `null` (73 ca, đã chuẩn hoá ở #918) · `""` (14) · `[]` (1).
 *
 * Bài học chung: trong một prompt, **MẪU là lời khai mạnh hơn LUẬT** — mắt đọc mẫu trước. Sửa luật
 * mà để mẫu mâu thuẫn thì luật thua.
 */
const ENVELOPE_SHAPE = [
  "{",
  `  "kind": "page" | "inFormAnswer" | "cancel" | "unknown",`,
  `  "page": "<id trang — CHỈ mã viết HOA đứng một mình sau '- ' ở DANH SÁCH, không kèm mô tả>",  // CHỈ khi kind = "page". Khi cancel/unknown: BỎ HẲN khoá này — KHÔNG viết null, KHÔNG viết "", KHÔNG viết [].`,
  `  "spans": [],                                     // mảng RỖNG là hợp lệ và thường gặp. Chỉ thêm phần tử khi CÓ chữ thật trong câu.`,
  `  "confidence": <0..1>,`,
  `  "ambiguities": [],                               // kẹt giữa ĐÚNG HAI trang ⇒ ["A","B"] và kind = "unknown"`,
  `  "multi": []                                      // câu có ≥2 việc ⇒ id các trang CÒN LẠI (việc chính ở "page")`,
  "}",
  "",
  "Mỗi phần tử của \"spans\" có đúng bốn khoá:",
  `  { "slot": "<tên ô>", "text": "<chữ NGUYÊN VĂN cắt từ câu>", "start": <vị trí bắt đầu>, "end": <vị trí kết thúc, không tính> }`,
].join("\n");

function renderStatic(pages: readonly AiCapabilityPage[]): string {
  const pinned = pinnedQuestions(pages);
  const catalog =
    pages.length === 0
      ? ["(Hiện KHÔNG có trang nào được phép — mọi câu đều trả kind = \"unknown\".)"]
      : pages.flatMap((p) => pageLines(p, pinned));
  return [
    "Bạn là NGƯỜI PHIÊN DỊCH của trợ lý Goppy (quỹ nhóm phụ huynh/câu lạc bộ ở Việt Nam).",
    "Việc của bạn CHỈ là: đọc câu người dùng ⇒ chọn ĐÚNG MỘT trang trong DANH SÁCH dưới ⇒ gạch chân chữ nguyên văn cho từng ô ⇒ nói độ chắc.",
    "Bạn KHÔNG tính toán, KHÔNG viết số tiền, KHÔNG tra tên người, KHÔNG kiểm quyền, KHÔNG trả lời người dùng — đó là việc của hệ thống.",
    "",
    /*
     * 🔴 US-AIV2-0053c — CẤM GHÉP/CHẾ ID, kèm HẬU QUẢ. Ca thật (24/09, `ONB-CREATE-FUND#08`): câu
     * `"mo quy su kien"` ⇒ model trả `"page": "COL-CREATE-FUND"` — một id **không có trong Sổ**,
     * ghép tiền tố `COL-` (họ trang đợt thu, đang nằm trong `ambiguities` của chính ca đó) với đuôi
     * `-CREATE-FUND`. Nó không chép sai, nó **chế ra**.
     *
     * Câu `"<id trang — phần NGAY SAU '- ' và TRƯỚC ':'>"` ở `ENVELOPE_SHAPE` (S23 đã thay: id nay đứng một mình
     * trên dòng mục — xem `pageLines`) nói đúng CHỖ LẤY nhưng **không cấm tự ghép**, và với model đang phân vân giữa mấy họ trang thì "lấy ở đâu" yếu hơn
     * "cấm chế". Cùng họ bài học ~dòng 145: model trả `"ONB-CREATE-GROUP [T1]"` vì **tuân thủ chính
     * xác thứ ta dặn** — lỗi nằm ở chỗ ta chưa dặn.
     *
     * Nói kèm hậu quả (bị loại, người dùng phải gõ lại) thay vì ra lệnh trần: một lệnh cấm có lý do
     * được tuân tốt hơn. Ca khoá ở `prompt.test.ts` đòi CẢ HAI vế.
     */
    /*
     * 🔴 US-AIV2-0053c — VẾ ĐÃ GỠ, ghi lại để không ai thêm về: bản đầu của câu này kết thúc bằng
     * *"Không chắc chọn trang nào thì để kind = \"unknown\", đừng đoán một id."*
     *
     * Đo được: `EXP-APPROVE` tụt intent 10000 → **9286**, và hai ca bỏ cuộc là `"duyệt đi"` +
     * `"duyệt chi đi"` — model trả `kind: "unknown"`, `confidence: 0.2`. Nó **tuân thủ chính xác thứ
     * ta dặn**: câu cụt ⇒ "không chắc" ⇒ `unknown`. Tôi đã dạy model DÈ DẶT QUÁ MỨC trong khi chỉ
     * muốn cấm nó GHÉP id — hai việc khác nhau, và vế thứ hai không phục vụ mục tiêu nào cả.
     *
     * Cùng cái bẫy mà chính file này ghi ở ~dòng 145 (model trả `"ONB-CREATE-GROUP [T1]"`): lỗi nằm ở
     * chỗ ta dặn, không ở model. Một câu thêm vào prompt phải trả lời được *"nó phục vụ mục tiêu nào"*
     * — nếu không thì nó chỉ là một cái giá phải trả ở chỗ khác.
     */
    `Id trang phải CHÉP NGUYÊN VĂN từ ${CATALOG_NAME}. KHÔNG được ghép, rút gọn, đổi tiền tố hay tự chế một id mới — kể cả khi bạn thấy một id "hợp lý hơn". Id không có trong danh sách sẽ bị loại bỏ hoàn toàn và người dùng phải gõ lại từ đầu.`,
    /*
     * 🔴 US-AIV2-0053c — VẾ THỨ HAI, CÙNG MỘT BỆNH. Sau khi thêm câu cấm cho `page`, thu lại bản ghi
     * thì `READ-PEOPLE-MINE` tụt span 10000 → 9231: model trả `chiSo = "danh sách thành viên"` cho câu
     * *"nhóm có những ai?"* — ĐÚNG VỀ NGHĨA nhưng **không có trong `choices`** (`thành viên | nhóm của
     * tôi | khoản của tôi | việc chờ tôi`), nên `judgeSpan` vứt span.
     *
     * Prompt ĐÃ in `chọn: a | b | c` cho ô enum, tức model **thấy danh sách mà vẫn không chép** — y hệt
     * bệnh của `page`. Một bản vá chỉ cấm ở `page` là vá **nửa miền**: cùng một hành vi "chế thay vì
     * chép" còn nguyên ở ô phân loại. Nói cả hai vế bằng cùng một giọng.
     *
     * ⚠️ Đây KHÔNG phải nới luật enum của 0054b (QĐ PO 19/09 §A-BIS): model vẫn được ÁNH XẠ
     * *"đội bóng công ty"* → *"thể thao"*; chỉ bắt buộc **giá trị cuối cùng phải là một phần tử của
     * `choices`**, đúng thứ `value_not_in_choices` đang canh.
     */
    'Với ô có "chọn: a | b | c": đặt lựa chọn vào "text" (chép nguyên văn một trong các lựa chọn), còn "slot" vẫn là TÊN Ô. Bạn được ánh xạ từ cách nói của người dùng sang lựa chọn gần nghĩa nhất, nhưng KHÔNG được viết một cách diễn đạt khác — giá trị ngoài danh sách sẽ bị vứt và trợ lý phải hỏi lại.',
    /*
     * 🔴 US-AIV2-0053c — VẾ THỨ BA: "slot" là TÊN Ô, không phải GIÁ TRỊ. Lộ ra ngay sau khi vế enum
     * chữa xong bệnh cũ — `READ-PEOPLE-MINE` vẫn span 9167 nhưng mã vi phạm ĐỔI từ
     * `value_not_from_sentence` sang `slot_not_in_page`: model trả `{"slot": "việc chờ tôi", ...}`,
     * tức nó lấy **giá trị** của ô đặt vào chỗ **tên** ô (4/17 ca; một ca còn tự chế `"danhSach"`).
     *
     * Và vế enum của tôi có thể đã GÓP PHẦN: nhấn *"giá trị phải là một trong các lựa chọn"* mà không
     * nói lựa chọn ấy đi vào đâu ⇒ model nhét nó vào `slot`. Đây đúng lớp *"vá prompt ⇒ hiệu ứng đa
     * chiều, phải thu lại TOÀN BỘ"*: một câu thêm vào có thể đẩy lỗi sang chỗ khác chứ không xoá nó.
     *
     * `ENVELOPE_SHAPE` chỉ viết `"<tên ô>"` — đúng nhưng mơ hồ với model đang phân vân. Nói cặp
     * slot/text thành một câu đối lập để không còn chỗ hiểu nhầm.
     */
    'Trong mỗi span: "slot" luôn là TÊN Ô (chép từ danh sách ô của trang, dạng chữ không dấu viết liền như "nhom", "chiSo"), "text" là chữ của người dùng. KHÔNG bao giờ đặt nội dung/giá trị vào "slot", và không tự đặt một tên ô mới.',
    "",
    "TRẢ VỀ DUY NHẤT một object JSON (không markdown, không lời dẫn, không khoá thừa) đúng khuôn:",
    ENVELOPE_SHAPE,
    "",
    // 0089 · tật 1 (55/97 ca): 35 ca thiếu ĐÚNG khoá `multi`, 20 ca thiếu cả cụm. Mảng rỗng phải được
    // nói là BẮT BUỘC, vì "không có gì để nói" là trạng thái THƯỜNG GẶP của ba khoá này.
    "BẮT BUỘC trả ĐỦ SÁU khoá mỗi lần: \"kind\", \"spans\", \"confidence\", \"ambiguities\", \"multi\" (và \"page\" khi kind = \"page\").",
    "- Không có gì để nói ở một khoá mảng thì vẫn phải viết mảng rỗng: \"spans\": [], \"ambiguities\": [], \"multi\": []. BỎ khoá đi là SAI.",
    // 0089 · tật 3 (15 ca `""`/`[]`, cùng họ 73 ca `null` đã chuẩn hoá ở #918): "bỏ khoá" ≠ "để rỗng".
    "- Ngược lại, \"page\" khi kind = \"cancel\"/\"unknown\" thì BỎ HẲN khoá — viết null, \"\" hay [] đều SAI.",
    "",
    "LUẬT GẠCH CHÂN (span):",
    "- \"text\" là chữ NGUYÊN VĂN cắt từ câu người dùng — giữ đúng từng ký tự, kể cả \"[SĐT]\" \"[STK]\" \"[EMAIL]\", không đổi số, không đổi chữ, không dịch, không chuẩn hoá.",
    "- \"start\"/\"end\" là vị trí ký tự của đoạn đó trong câu ([start, end)).",
    "- Mỗi span trỏ một ô CÓ TRONG trang đã chọn. Không thấy chữ cho ô nào thì KHÔNG bịa span — hệ thống sẽ hỏi.",
    // 0089 · tật 2 (25/97 ca): model điền ĐỦ ô của trang, ô nào không có chữ thì để `text: ""`, `end: 0`
    // làm chỗ giữ chỗ — hành vi hợp lý nếu mẫu cũ ngụ ý "mỗi ô một span". Phải cấm BẰNG CHỮ, vì đây là
    // tật của người ngoan, không phải của người ẩu.
    "- KHÔNG điền span rỗng làm chỗ giữ chỗ: \"text\": \"\" hay \"end\": 0 đều SAI. Ô nào không có chữ thì BỎ HẲN span của ô đó, đừng để lại vỏ.",
    "- Không lặp cùng một \"slot\" hai lần trong \"spans\".",
    // 🔴 0084 — vế NẶNG nhất đo được trên 40 bản ghi LLM thật. Trang khai `(không có ô nào)` vẫn nhận span với
    // `slot` tự chế: `"ví dụ"`, `"nhóm"`, `"tk"`, thậm chí chính ID TRANG. `AI_SLOT_ID_RE` từ chối ⇒ CẢ PHONG BÌ
    // trượt `.strict()` ⇒ `envelope_invalid` ⇒ trang mất SẠCH điểm intent dù đã chọn ĐÚNG TÊN TRANG.
    // Ca rõ nhất: `GRP-OWNERSHIP` đúng trang 5/5 mà chấm ra **intent 0 bps · 5 vi phạm safety**.
    // Câu "không bịa span" ở trên nói về Ô KHÔNG CÓ CHỮ; nó KHÔNG phủ ca TRANG KHÔNG CÓ Ô NÀO — hai chuyện khác nhau.
    "- Trang ghi \"(không có ô nào)\" thì \"spans\" PHẢI là [] — tuyệt đối không đặt tên ô mới, không dùng id trang, không dùng chữ \"ví dụ\" làm tên ô.",
    "- \"slot\" CHỈ được là một tên ô in ở dòng \"ô:\" của trang đã chọn, chép đúng từng ký tự. Không có tên nào khớp ⇒ bỏ span đó đi.",
    "- Bạn không được tự tính hay viết số tiền, tên, ngày, id ở bất kỳ khoá nào ngoài \"text\" của span.",
    "",
    "LUẬT CHỌN TRANG:",
    // 0084: tập ĐÓNG nhắc lại NGAY TRƯỚC luật dùng nó. Đo được: 5/40 lượt trả `kind: "redirect"` — một chữ
    // model mượn từ nhãn bậc, không phải từ tập này. `kind` nói LOẠI CÂU TRẢ LỜI, bậc của trang không liên quan.
    "- \"kind\" CHỈ được là một trong BỐN: \"page\", \"inFormAnswer\", \"cancel\", \"unknown\". Bậc việc của trang (đọc/T1/T2/chuyển màn) KHÔNG phải một \"kind\" — trang bậc nào cũng dùng kind = \"page\".",
    "- Câu khớp một trang ⇒ kind = \"page\" + \"page\" = id đó.",
    "- Không trang nào khớp ⇒ kind = \"unknown\", \"ambiguities\" = [] (KHÔNG chọn trang gần giống).",
    "- Kẹt giữa ĐÚNG hai trang ⇒ kind = \"unknown\", \"ambiguities\" = [A, B]. Kẹt giữa ≥3 trang ⇒ kind = \"unknown\", \"ambiguities\" = [].",
    "- Người dùng muốn dừng/huỷ (\"thôi\", \"bỏ đi\", \"huỷ\", \"không làm nữa\") ⇒ kind = \"cancel\", \"spans\" = [].",
    "- Câu có ≥2 việc ⇒ \"page\" = việc chính (nêu trước), \"multi\" = id các trang còn lại.",
    /*
     * 🔴 AI-SMOOTH-4 — DANH SÁCH TRANG ĐỨNG SAU CÙNG của phần tĩnh: mọi chữ phía trên giống hệt nhau ở MỌI lượt (không phụ thuộc trang tìm
     * được) ⇒ thành một TIỀN TỐ cố định mà nhà cung cấp cache được (OpenAI tự cache tiền tố ≥1.024 token; Anthropic cần `cache_control` —
     * lượt `interpret` cố ý không đổi khuôn request, xem `claude-llm-client.ts`). Danh sách in trước luật thì tiền tố chung chỉ còn 3 câu
     * chào. Lượt trước (`recentTurns`) và khối form vẫn nối SAU danh sách — luật của form là thứ model đọc sau cùng (xem `system`).
     */
    "",
    LABEL.catalogHeading,
    ...catalog,
  ].join("\n");
}

function renderInForm(ctx: InterpreterFormContext): string {
  const asked = ctx.askedSlot ? `${LABEL.formAsked} ${ctx.askedSlot}.` : "Không có ô nào đang hỏi (đang ở bước xác nhận).";
  const filled = `${LABEL.formFilled} ${ctx.filledSlots.length > 0 ? ctx.filledSlots.join(", ") : "(chưa có)"}.`;
  return [
    "",
    `NGƯỜI DÙNG ĐANG GIỮA FORM của trang ${ctx.page}. ${asked} ${filled}`,
    "Phân câu vào ĐÚNG MỘT trong bốn loại:",
    `1. Trả lời ô ⇒ kind = "inFormAnswer", "page" bỏ trống, span cho ô đang hỏi.`,
    `2. Sửa lại một ô đã điền ⇒ kind = "inFormAnswer", span cho ô đó (ô phải nằm trong danh sách đã điền hoặc đang hỏi).`,
    `3. Huỷ ⇒ kind = "cancel".`,
    `4. Việc chen (một việc KHÁC trang đang mở) ⇒ kind = "page" với id trang mới — hệ thống quyết thứ tự, bạn chỉ báo.`,
  ].join("\n");
}

/**
 * Tạo bộ dựng prompt có cache phần tĩnh. Cổng api giữ MỘT instance (singleton Nest); eval có thể tạo instance riêng.
 */
export function createInterpreterPromptBuilder(): InterpreterPromptBuilder {
  const cache = new WeakMap<readonly AiCapabilityPage[], string>();
  let renders = 0;
  return {
    get staticRenders() {
      return renders;
    },
    system(pages, ctx) {
      let base = cache.get(pages);
      if (base === undefined) {
        base = renderStatic(pages);
        renders += 1;
        cache.set(pages, base);
      }
      // Thứ tự có chủ đích: lượt trước (văn cảnh) ĐỨNG TRƯỚC luật phân loại của form — luật phải là
      // thứ model đọc sau cùng, gần chỗ ra quyết định nhất.
      const recent = renderRecentTurns(ctx?.recentTurns ?? []);
      const withRecent = recent === "" ? base : base + "\n" + recent;
      return ctx?.inForm ? withRecent + "\n" + renderInForm(ctx.inForm) : withRecent;
    },
  };
}
